'use client';

import { PhoneIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReactComponent as MapLocationIcon } from '@/app/_icons/CkMapIcon.svg';
import {
  DealershipContactInfo,
  DealershipProps,
} from '@/types/dealership/dealership.types';

import {
  DisplayOperatingHour,
  formatDealerPhoneNumberForTel,
  groupOperatingHours,
  hasValidCoordinates,
} from './dealershipDateUtils';
import styles from './FindUsSection.module.scss';

const DealershipMap = dynamic(() => import('./DealershipMapLoader'), {
  loading: () => (
    <Box height="400px" bg="gray.100">
      <Text>Loading map...</Text>
    </Box>
  ),
  ssr: false,
});

interface Props {
  dealerInfo: DealershipContactInfo | DealershipProps;
}

const FindUsSection = ({ dealerInfo }: Props) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isMapInView, setIsMapInView] = useState(false);

  const groupedOperatingHours = useMemo(
    () => groupOperatingHours(dealerInfo?.operatingHours),
    [dealerInfo?.operatingHours],
  );

  const phoneNumberForTel = useMemo(
    () => formatDealerPhoneNumberForTel(dealerInfo?.phoneNumber),
    [dealerInfo?.phoneNumber],
  );

  const checkMapInView = useCallback(() => {
    if (mapRef.current) {
      const rect = mapRef.current.getBoundingClientRect();

      setIsMapInView(
        (wasMapInView) =>
          wasMapInView || (rect.top < window.innerHeight && rect.bottom >= 0),
      );
    }
  }, []);

  useEffect(() => {
    checkMapInView();
    window.addEventListener('scroll', checkMapInView, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkMapInView);
    };
  }, [checkMapInView]);

  const fullAddress = [
    dealerInfo?.mainAddress,
    dealerInfo?.secondaryAddress,
    dealerInfo?.landmark,
    dealerInfo?.city?.name,
    dealerInfo?.city?.province?.name,
    dealerInfo?.code,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Container
      maxW="100%"
      className={styles.mapWrapper}
      paddingX={{ base: '6', md: '14' }}
      paddingTop={{ base: '14', md: '50px' }}
      paddingBottom={{ base: 14, md: '50px' }}
      id="contact-section"
    >
      <Suspense fallback={<Text>Loading contact details...</Text>}>
        <div className={styles.headingElement}>
          <Heading
            as="h2"
            className="titleLg"
            textAlign={{ base: 'center', lg: 'start' }}
            mb={{ base: 8, md: '45px' }}
          >
            Find Us
          </Heading>
        </div>
        <SimpleGrid
          columns={[1, 1, 2, 3]}
          maxWidth={{ base: '100%', md: '1440px' }}
          spacingX={{ base: 0, md: '40px' }}
          spacingY={{ base: 8, md: '40px' }}
          className={styles.mapContactDetails}
          margin={'auto'}
        >
          <Box ref={mapRef} minHeight={{ base: '300px', md: 'auto' }}>
            {isMapInView && hasValidCoordinates(dealerInfo) && (
              <DealershipMap dealerInfo={dealerInfo} />
            )}
            {!isMapInView && (
              <Box
                height="300px"
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text>Scroll to load map...</Text>
              </Box>
            )}
            {isMapInView && !hasValidCoordinates(dealerInfo) && (
              <Box
                height="300px"
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text>Map data unavailable.</Text>
              </Box>
            )}
          </Box>

          <Flex direction={'column'} width={'100%'} justify="flex-start">
            <Box
              className={styles.headingElement}
              width={'100%'}
              maxWidth={{ base: '100%', sm: '400px', md: 'auto' }}
              mb={{ base: 8, md: 0 }}
            >
              <Heading as="h3" className={styles.titleMd} marginBottom={4}>
                Physical Address
              </Heading>
              <Flex
                as="a"
                dir="row"
                gap={3}
                alignItems="flex-start"
                href={
                  dealerInfo?.google_address ||
                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <Box mt={1} flexShrink={0}>
                  <MapLocationIcon
                    className={styles.locationIcon}
                    width={28}
                    height={28}
                  />
                </Box>
                <Text fontSize={'14px'} lineHeight={'19.6px'}>
                  {fullAddress || 'Address not available'}
                </Text>
              </Flex>

              <Heading
                as="h3"
                className={styles.titleMd}
                marginTop={8}
                marginBottom={4}
              >
                Contact Numbers
              </Heading>
              {dealerInfo?.phoneNumber && (
                <Flex dir="row" alignItems="center" gap={2} marginBottom={4}>
                  <PhoneIcon
                    className={styles.phoneIcon}
                    width={'13px'}
                    height={'13px'}
                  />
                  <Text
                    as="a"
                    href={`tel:+27${phoneNumberForTel}`}
                    fontSize={'14px'}
                    lineHeight={'19.6px'}
                  >
                    {dealerInfo.phoneNumber}
                  </Text>
                </Flex>
              )}
              <Flex
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                gap={2}
              >
                <Text as={'b'} fontSize={'14px'} lineHeight={'19.6px'}>
                  Emergency:
                </Text>
                <Text
                  as="a"
                  href={'tel:+27861542000'}
                  fontSize={'14px'}
                  lineHeight={'19.6px'}
                >
                  086 154 2000
                </Text>
              </Flex>
            </Box>
          </Flex>

          <Flex direction={'row'} width={'100%'} justify="flex-start">
            <Box
              className={styles.headingElement}
              width={'100%'}
              maxWidth={{ base: '100%', sm: '400px', md: 'auto' }}
            >
              <Flex>
                <Divider
                  orientation="vertical"
                  borderLeftWidth={'2px'}
                  marginRight={{ base: 4, sm: 6 }}
                  className={styles.borderLeft}
                  borderColor="gray.300"
                  display={{ base: 'none', md: 'block' }}
                />
                <Flex direction="column" width="100%">
                  <Heading as="h3" className={styles.titleMd} marginBottom={4}>
                    Showroom Hours
                  </Heading>
                  {groupedOperatingHours.length > 0 ? (
                    groupedOperatingHours.map(
                      (item: DisplayOperatingHour, index: number) => (
                        <Flex
                          key={`${item.day}-${index}`}
                          direction={'row'}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                          mb={1.5}
                          width="100%"
                        >
                          <Text
                            as={'b'}
                            fontSize={'14px'}
                            lineHeight={'19.6px'}
                            flexShrink={0}
                            mr={2}
                          >
                            {item.day}
                          </Text>
                          <Text
                            fontSize={'14px'}
                            lineHeight={'19.6px'}
                            textAlign="right"
                          >
                            {item.closed
                              ? 'Closed'
                              : `${item.openTime} - ${item.closeTime}`}
                          </Text>
                        </Flex>
                      ),
                    )
                  ) : (
                    <Text fontSize={'14px'} lineHeight={'19.6px'}>
                      Operating hours not available.
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </SimpleGrid>
      </Suspense>
    </Container>
  );
};

export default FindUsSection;
